import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  approveAssetDisposal,
  listAssetEvents,
  listAssets,
  recordConditionChange,
  registerAsset,
  requestAssetDisposal,
} from '../../api/assets';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDate,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Asset, AssetCondition, AssetEvent } from '../../types/domain';

const CONDITION_LABEL: Record<AssetCondition, string> = { GOOD: 'Baik', FAIR: 'Cukup', POOR: 'Buruk' };
const EVENT_LABEL: Record<string, string> = {
  ACQUIRED: 'Didaftarkan',
  CONDITION_CHANGED: 'Perubahan Kondisi',
  MAINTENANCE_COMPLETED: 'Perawatan Selesai',
  DISPOSAL_REQUESTED: 'Pengajuan Pelepasan',
  DISPOSED: 'Dilepas/Dihapus',
};

/**
 * Asset Register (FASE 11, migration 0048). Duplicate asset_tag is rejected
 * at the DB layer (fn_register_asset), not just here. History (asset_events)
 * is append-only — no edit/delete UI exists because none is possible
 * server-side (fn_block_asset_events_mutation blocks it).
 */
export default function AssetsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, branches } = useBranch();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<Asset | null>(null);

  const canCreate = hasPermission('asset', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setAssets(await listAssets());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const branchName = (id: string) => branches.find((b) => b.branch_id === id)?.name ?? '—';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q) && !a.asset_tag.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && a.status !== statusFilter) return false;
      return true;
    });
  }, [assets, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);

  const activeCount = assets.filter((a) => a.status === 'ACTIVE').length;
  const maintCount = assets.filter((a) => a.status === 'UNDER_MAINTENANCE').length;
  const disposedCount = assets.filter((a) => a.status === 'DISPOSED').length;

  return (
    <div>
      <PageHeader title="Aset" subtitle="Register aset — tag, lokasi cabang, kondisi, riwayat. Migrasi 0048." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Daftarkan aset'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewAssetForm
          branchId={activeBranchId}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Aset', value: assets.length },
              { label: 'Aktif', value: activeCount, tone: 'success' as const },
              { label: 'Dalam Perawatan', value: maintCount, tone: 'info' as const },
              { label: 'Dilepas', value: disposedCount },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama/tag aset..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 180 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Aktif</option>
                <option value="UNDER_MAINTENANCE">Dalam Perawatan</option>
                <option value="DISPOSED">Dilepas</option>
              </Select>
            }
          />
          {assets.length === 0 && <EmptyState text="Belum ada aset terdaftar." />}
          {assets.length > 0 && total === 0 && <EmptyState text="Tidak ada aset yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tag Aset</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Cabang</th>
                  <th style={thStyle}>Kondisi</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => (
                  <ClickableRow key={a.asset_id} onClick={() => setSelected(a)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{a.asset_tag}</td>
                    <td style={tdStyle}>{a.name}</td>
                    <td style={tdStyle}>{branchName(a.branch_id)}</td>
                    <td style={tdStyle}>{CONDITION_LABEL[a.condition]}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={a.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <AssetDetailDrawer
        asset={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        branchName={selected ? branchName(selected.branch_id) : ''}
        canUpdate={hasPermission('asset', 'update')}
        canDispose={hasPermission('asset', 'dispose')}
        canApprove={hasPermission('asset', 'approve')}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewAssetForm({ branchId, onCreated }: { branchId: string; onCreated: () => void }) {
  const [assetTag, setAssetTag] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState('');
  const [acquisitionCost, setAcquisitionCost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await registerAsset(
        branchId,
        assetTag.trim(),
        name.trim(),
        category.trim() || null,
        acquisitionDate || null,
        acquisitionCost ? Number(acquisitionCost) : null
      );
      onCreated();
    } catch (err) {
      // Duplicate asset_tag is rejected server-side (fn_register_asset) — surfaced here verbatim, not reworded.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 140 }}>
        <FieldLabel label="Tag Aset">
          <TextInput value={assetTag} onChange={(e) => setAssetTag(e.target.value)} required autoFocus placeholder="AST-001" />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Nama Aset">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required placeholder="Forklift / AC / Kompresor" />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Kategori (opsional)">
          <TextInput value={category} onChange={(e) => setCategory(e.target.value)} placeholder="HVAC / Kendaraan" />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Tanggal Perolehan">
          <TextInput type="date" value={acquisitionDate} onChange={(e) => setAcquisitionDate(e.target.value)} />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Biaya Perolehan">
          <TextInput type="number" min="0" value={acquisitionCost} onChange={(e) => setAcquisitionCost(e.target.value)} placeholder="0" />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function AssetDetailDrawer({
  asset,
  open,
  onClose,
  branchName,
  canUpdate,
  canDispose,
  canApprove,
  onChanged,
}: {
  asset: Asset | null;
  open: boolean;
  onClose: () => void;
  branchName: string;
  canUpdate: boolean;
  canDispose: boolean;
  canApprove: boolean;
  onChanged: () => void;
}) {
  const [events, setEvents] = useState<AssetEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [newCondition, setNewCondition] = useState<AssetCondition>('GOOD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!asset) return;
    setError(null);
    setNewCondition(asset.condition);
    setLoadingEvents(true);
    listAssetEvents(asset.asset_id)
      .then(setEvents)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoadingEvents(false));
  }, [asset?.asset_id]);

  if (!asset) return null;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`${asset.asset_tag} — ${asset.name}`}>
      <DrawerField label="Cabang" value={branchName} />
      <DrawerField label="Kategori" value={asset.category ?? '—'} />
      <DrawerField label="Kondisi" value={CONDITION_LABEL[asset.condition]} />
      <DrawerField label="Status" value={<StatusBadge status={asset.status} />} />
      <DrawerField label="Tanggal Perolehan" value={formatDate(asset.acquisition_date)} />
      {asset.disposal_requested_by && !asset.disposal_approved_by && (
        <DrawerField label="Pengajuan Pelepasan" value="Menunggu persetujuan (maker-checker)" />
      )}
      {asset.status === 'DISPOSED' && <DrawerField label="Dilepas Pada" value={formatDateTime(asset.disposed_at)} />}

      {canUpdate && asset.status !== 'DISPOSED' && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <FieldLabel label="Catat Perubahan Kondisi">
            <div style={{ display: 'flex', gap: 8 }}>
              <Select value={newCondition} onChange={(e) => setNewCondition(e.target.value as AssetCondition)} style={{ flex: 1 }}>
                {Object.entries(CONDITION_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
              <Button
                variant="secondary"
                loading={busy}
                onClick={() => void run(() => recordConditionChange(asset.asset_id, newCondition).then(() => undefined))}
              >
                Simpan
              </Button>
            </div>
          </FieldLabel>
        </div>
      )}

      {canDispose && asset.status === 'ACTIVE' && !asset.disposal_requested_by && (
        <Button
          variant="secondary"
          loading={busy}
          onClick={() => void run(() => requestAssetDisposal(asset.asset_id))}
          style={{ marginTop: 12 }}
        >
          Ajukan Pelepasan Aset
        </Button>
      )}
      {canApprove && asset.disposal_requested_by && !asset.disposal_approved_by && (
        <Button
          variant="primary"
          loading={busy}
          onClick={() => void run(() => approveAssetDisposal(asset.asset_id))}
          style={{ marginTop: 12 }}
        >
          Setujui Pelepasan Aset
        </Button>
      )}
      {error && <ErrorNote text={error} />}

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
        <GroupLabel>Riwayat Aset</GroupLabel>
        {loadingEvents && <LoadingNote />}
        {!loadingEvents && events.length === 0 && <EmptyState text="Belum ada riwayat." variant="section" />}
        {events.map((ev) => (
          <div key={ev.event_id} style={{ padding: '8px 0', borderBottom: '1px solid var(--alex-border-soft)', fontSize: 'var(--alex-fs-body)' }}>
            <div style={{ fontWeight: 600 }}>{EVENT_LABEL[ev.event_type] ?? ev.event_type}</div>
            <div style={{ color: 'var(--alex-text-secondary)' }}>{formatDateTime(ev.occurred_at)}</div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}
